/* The tray: a horizontally scrolling strip of chips between the text and the tool row — what is
   going to be sent with this message, in the order it will be sent. The chip itself is `ui/`'s
   (`.tb-chip`): what is declared here is the strip, the two things that hang off a chip (the
   upload's progress, along its foot) and what stands under one (a caption, a refusal). Every colour
   and space is a token, and every control is at least --target-min in both directions (WCAG
   2.5.8). */

.tb-tray {
	display: flex;
	flex-direction: column;
	gap: var(--space-1);
	padding: 0 var(--space-3) var(--space-1);
}

.tb-tray__list {
	display: flex;
	align-items: flex-start;
	gap: var(--space-2);
	margin: 0;
	padding: 0;
	list-style: none;
	overflow-x: auto;
	/* A strip a mouse can scroll must be reachable by a keyboard too, and a scroll container with
	   no focusable child is a WCAG failure — the chips inside it are focusable, so it is not. */
	scrollbar-width: thin;
}

/* One file: the chip, and whatever belongs to that file under it. */
.tb-tray__item {
	display: flex;
	flex-direction: column;
	gap: var(--space-1);
	min-inline-size: 0;
	max-inline-size: 18rem;
}

/* The pill is `.tb-chip`; it gives back the end padding a trailing close button does not need, and
   it clips the progress hairline to its own round corners. */
.tb-tray__chip {
	position: relative;
	padding: 0 var(--space-1) 0 var(--space-2);
	max-inline-size: 100%;
	overflow: hidden;
}

.tb-tray__chip--failed {
	border-color: var(--color-danger);
}

.tb-tray__button {
	display: flex;
	align-items: center;
	gap: var(--space-2);
	min-height: var(--target-min);
	max-width: 100%;
	overflow: hidden;
	text-align: start;
	white-space: nowrap;
	text-overflow: ellipsis;
	font-weight: inherit;
}

.tb-tray__thumb {
	inline-size: 1rem;
	block-size: 1rem;
	object-fit: cover;
	border-radius: 2px;
	flex: none;
}

.tb-tray__size {
	color: var(--color-text-subtle);
	font-size: var(--text-meta);
	font-weight: 400;
	white-space: nowrap;
}

.tb-tray__remove {
	flex: none;
}

.tb-tray__caption {
	color: var(--color-text-muted);
	font-size: var(--text-meta);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Where a file came from — "Pasted text · 1,240 lines · 1 secret hidden" — quieter than a caption,
   because it is the composer's sentence and not the sender's. It may take two lines rather than
   hide the one fact that matters most in it. */
.tb-tray__note {
	color: var(--color-text-subtle);
	font-size: var(--text-meta);
}

/* How far the upload has got, as a hairline along the chip's foot: the one place a progress bar
   belongs in a pill, and the accent is what says "still working". */
.tb-tray__progress {
	position: absolute;
	inset-inline: 0;
	inset-block-end: 0;
	block-size: 2px;
	background: var(--color-border-subtle);
}

.tb-tray__progressBar {
	display: block;
	block-size: 100%;
	background: var(--color-accent);
	transition: inline-size var(--duration-fast) linear;
}

.tb-tray__error {
	margin: 0;
	color: var(--color-danger);
	font-size: var(--text-sm);
}

.tb-composer__dropzone {
	position: absolute;
	inset: 0;
	z-index: 2;
	display: grid;
	place-items: center;
	border: 2px dashed var(--color-accent);
	border-radius: var(--radius-lg);
	background: var(--color-surface);
	color: var(--color-text);
	pointer-events: none;
}
